import { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "src/redux/hooks";
import { RootState } from "src/redux/store";
import { listIPLClaims } from "src/app/IPL/iplAppSlice";
import { TableProps } from "@amzn/awsui-components-console";
import { MetricsPublisher } from "src/metrics";
import { IntlProductLiabilityClaimSummary } from "@amzn/ttechclaimintakeservice-client";
import SearchResults from "../searchResults/SearchResults";
import ExportToCSVButton from "../searchResults/ExportToCSVButton";
import ClaimsSearchBar from "../claimsSearchBar/ClaimsSearchBar";
import { Flashbar, FlashbarProps } from "@amzn/awsui-components-console";
import SimLinkGenerator from "../simLinkGenerator/SimLinkGenerator";
import { SIM_TYPES } from "../simLinkGenerator/types";
import { SEARCH_PARAMETERS } from "../searchResults/constants";
import { IPL_LINE_OF_BUSINESS_CONFIG, CLAIMS_LIMIT } from "./constants";
import { EMPTY_STRING_INDICATOR } from "../../app/constants";

// Declare metricsPublishers, they will be initialized on first render
let metricsPublisher: MetricsPublisher;
let metricsPublisherSearchByOrderId: MetricsPublisher;

interface SearchParameters {
  searchBy: string;
  searchTerm: string;
  startDate?: number;
  endDate?: number;
}

export interface IPLClaimsHistoryProps {
  /** Whether to show only claims submitted by the current user */
  showUserClaimsOnly?: boolean;
  /** Custom test ID for the component */
  testId?: string;
}

// Column definitions for IPL claims based on requirements
const IPL_CLAIM_COLUMN_DEFINITIONS: TableProps.ColumnDefinition<IntlProductLiabilityClaimSummary>[] =
  [
    {
      id: "claimNumber",
      header: "Claim Number",
      cell: (item) => item.claimNumber ?? EMPTY_STRING_INDICATOR,
      sortingField: "claimNumber",
    },
    {
      id: "orderId",
      header: "Order ID",
      cell: (item) => item.orderId ?? EMPTY_STRING_INDICATOR,
      sortingField: "orderId",
    },
    {
      id: "claimReporterAlias",
      header: "Claim Submitter",
      cell: (item) => item.claimReporterAlias ?? EMPTY_STRING_INDICATOR,
      sortingField: "claimReporterAlias",
    },
    {
      id: "itemPurchasedMarketplace",
      header: "Marketplace",
      cell: (item) => item.itemPurchasedMarketplace ?? EMPTY_STRING_INDICATOR,
      sortingField: "itemPurchasedMarketplace",
    },
    {
      id: "incidentDate",
      header: "Incident Date",
      cell: (item) => item.incidentDate ?? EMPTY_STRING_INDICATOR,
      sortingField: "incidentDate",
    },
    {
      id: "createdAtTimestamp",
      header: "Submitted Date",
      cell: (item) =>
        item.createdAtTimestamp
          ? new Date(item.createdAtTimestamp * 1000).toISOString().split("T")[0]
          : EMPTY_STRING_INDICATOR,
      sortingField: "createdAtTimestamp",
    },
  ];

// CSV headers for IPL claims export
const IPL_CSV_HEADERS = [
  { label: "Claim Number", key: "claimNumber" },
  { label: "Order ID", key: "orderId" },
  { label: "Claim Submitter", key: "claimReporterAlias" },
  { label: "Marketplace", key: "itemPurchasedMarketplace" },
  { label: "Incident Date", key: "incidentDate" },
  { label: "Submitted Date", key: "createdAtTimestamp" },
];

/**
 * IPL specific implementation of claims history search
 * Uses the generic SearchResults component with IPL-specific configuration
 * @param props IPLClaimsHistoryProps
 * @returns IPLClaimsHistory component
 */
function IPLClaimsHistory({
  showUserClaimsOnly = false,
  testId = "ipl-claims-search-results",
}: IPLClaimsHistoryProps) {
  // Redux state
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { user } = useAppSelector((state: RootState) => state.auth);
  const { iplClaimsList, fetchingClaimsList, listIPLClaimsError } =
    useAppSelector((state: RootState) => state.iplApp);

  const [urlSearchParams] = useSearchParams();
  const [flashbarItems, setFlashBarItems] = useState<
    FlashbarProps.MessageDefinition[]
  >([]);

  const [displayData, setDisplayData] = useState<
    IntlProductLiabilityClaimSummary[]
  >([]);

  // IPL-specific search callback
  const handleSearch = (searchParams: SearchParameters) => {
    const searchRoute =
      IPL_LINE_OF_BUSINESS_CONFIG.routePath +
      `?${searchParams.searchBy}=${searchParams.searchTerm}`;
    navigate(searchRoute, { replace: true });

    // Trigger search with the search parameters
    triggerSearch(searchParams);
  };

  // IPL-specific navigation callback for top navigation
  const handleNavigate = (route: string) => {
    window.open(route, "_blank", "noreferrer");
  };

  // IPL-specific validation callback (no special validation needed for IPL)
  const validateSearchTerm = (): string | null => {
    return null; // No special validation for IPL claims
  };

  // IPL-specific metrics callback
  const publishMetrics = (searchTerm: string, searchBy: SEARCH_PARAMETERS) => {
    // Use generic counter metrics for IPL search tracking
    if (searchBy === SEARCH_PARAMETERS.ORDER_ID) {
      metricsPublisherSearchByOrderId.publishIPLClaimsSearchByOrderId();
    }
    metricsPublisher.publishIPLClaimsSearchBarUsed();
  };

  const triggerSearch = (searchParameters: SearchParameters) => {
    const { searchTerm } = searchParameters;

    // TODO: This code below is to implement get claims by submitter alias
    // if (showUserClaimsOnly && user.id) {
    //   dispatch(
    //     listIPLClaims({
    //       input: {},
    //     })
    //   );
    //   return;
    // }
    dispatch(
      listIPLClaims({
        input: {
          orderId: searchTerm,
        },
      })
    );
  };

  useEffect(() => {
    // Initialize metrics publishers
    metricsPublisher = new MetricsPublisher("iplSearch");
    metricsPublisherSearchByOrderId = new MetricsPublisher(
      "iplSearchByOrderId"
    );

    if (showUserClaimsOnly) {
      // For user claims, trigger search when user is loaded
      if (user.id) {
        triggerSearch({ searchBy: "", searchTerm: "" });

        // Initialize metrics for user claims
        metricsPublisher = new MetricsPublisher("mySubmittedIPLClaims");
        metricsPublisher.publishIPLMySubmittedClaimsVisited();
      }
    } else {
      // Regular search results logic
      const orderId = urlSearchParams.get("orderId");
      const searchParameters: SearchParameters = {
        searchBy: SEARCH_PARAMETERS.ORDER_ID,
        searchTerm: orderId ?? "",
      };
      triggerSearch(searchParameters);

      metricsPublisher = new MetricsPublisher("iplSearchResultsVisited");
    }
  }, [showUserClaimsOnly, user.id, urlSearchParams]);

  // Update display data when Redux state changes
  useEffect(() => {
    if (iplClaimsList && iplClaimsList.length > 0) {
      setDisplayData(iplClaimsList);
    }
  }, [iplClaimsList]);

  // Set up error flashbar for user claims
  useEffect(() => {
    if (showUserClaimsOnly && listIPLClaimsError) {
      setFlashBarItems([
        {
          type: "error",
          dismissible: true,
          dismissLabel: "Dismiss message",
          content: (
            <span>
              There was an error loading your submitted IPL claims. Please
              reload the page. If the error persists please open a{" "}
              <SimLinkGenerator
                simType={SIM_TYPES.REPORT_A_BUG}
                target="_blank"
                ariaLabel="Pressing this link will open a new tab where you will be able to create a ticket for the team to investigate this issue."
              >
                support ticket
              </SimLinkGenerator>{" "}
              to get help with this issue.
            </span>
          ),
          id: "message_1",
          onDismiss: () => setFlashBarItems([]),
        },
      ]);
    } else {
      setFlashBarItems([]);
    }
  }, [showUserClaimsOnly, listIPLClaimsError]);

  // Make sure user is loaded before logging metrics for regular search
  useEffect(() => {
    if (!showUserClaimsOnly && user.siteCode) {
      metricsPublisher.publishIPLSearchResultsVisited();
    }
  }, [showUserClaimsOnly, user]);

  // Generate header content with IPL-specific messaging
  const getHeaderContent = () => {
    if (showUserClaimsOnly) {
      const baseText = "My Submitted IPL Claims";
      const limitText =
        displayData?.length >= CLAIMS_LIMIT
          ? ` (Most Recent ${CLAIMS_LIMIT})`
          : "";
      return `${baseText}${limitText}`;
    }

    return "IPL Claims Search Results";
  };

  // Generate header actions with IPL-specific export functionality
  const getHeaderActions = () => {
    const filePrefix = showUserClaimsOnly
      ? "my-submitted-ipl-claims"
      : "ipl_claims_search_results";
    return (
      <ExportToCSVButton
        data={displayData as any[]}
        headers={IPL_CSV_HEADERS}
        fileNamePrefix={filePrefix}
      />
    );
  };

  // Generate search bar component (only for regular search)
  const getSearchBarComponent = () => {
    if (showUserClaimsOnly) {
      return null; // No search bar for user claims
    }
    return (
      <ClaimsSearchBar
        topNavigation={false}
        lineOfBusinessConfig={IPL_LINE_OF_BUSINESS_CONFIG}
        onSearch={handleSearch}
        onNavigate={handleNavigate}
        validateSearchTerm={validateSearchTerm}
        publishMetrics={publishMetrics}
        maxSearchTermLength={25}
      />
    );
  };

  // Generate flashbar for user claims errors
  const getFlashbarComponent = () => {
    if (showUserClaimsOnly && listIPLClaimsError && flashbarItems.length > 0) {
      return <Flashbar data-testid="error-flashbar" items={flashbarItems} />;
    }
    return null;
  };

  return (
    <>
      {getFlashbarComponent()}
      <SearchResults<IntlProductLiabilityClaimSummary>
        items={displayData}
        columnDefinitions={IPL_CLAIM_COLUMN_DEFINITIONS}
        loading={fetchingClaimsList}
        loadingText="Loading IPL Claims"
        resourceName="IPL Claim"
        headerContent={getHeaderContent()}
        headerActions={getHeaderActions()}
        searchBarComponent={getSearchBarComponent()}
        filterPlaceholder="Filter IPL claims"
        filterAriaLabel="Filter IPL claims"
        defaultSorting={{
          sortingField: "createdAtTimestamp",
          isDescending: true,
        }}
        pageSize={10}
        minItemsForControls={5}
        testId={testId}
      />
    </>
  );
}

export default IPLClaimsHistory;
